import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Col, Empty, Form, Input, InputNumber, Modal, Row, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CrownOutlined } from '@ant-design/icons';
import { get, post, put, del } from '../../../api/client';
import type { LevelRow } from '../types';

const TEAL = '#0d9488';
/** 等级卡片配色（按序循环），营造现代卡片视觉而非表格堆砌 */
const PALETTE = ['#0d9488', '#6366f1', '#f59e0b', '#ec4899', '#10b981', '#3b82f6'];

/**
 * 会员等级管理（卡片式 CRUD）。
 * - GET/POST/PUT/DELETE /admin/membership/levels
 * - 请求体 camelCase，由后端 RequestInterceptor 转 snake_case 绑定 DTO。
 */
export function LevelsTab() {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<LevelRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const { data, isLoading } = useQuery({
    queryKey: ['membership-levels'],
    queryFn: () => get<LevelRow[]>('/admin/membership/levels'),
  });
  const levels = data ?? [];

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['membership-levels'] });
    void qc.invalidateQueries({ queryKey: ['membership-compare'] });
  };

  const save = useMutation({
    mutationFn: ({ code, values }: { code?: string; values: Record<string, unknown> }) =>
      code ? put(`/admin/membership/levels/${code}`, values) : post('/admin/membership/levels', values),
    onSuccess: () => {
      message.success('等级已保存');
      setEditing(null);
      setCreating(false);
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '保存失败'),
  });

  const remove = useMutation({
    mutationFn: (code: string) => del(`/admin/membership/levels/${code}`),
    onSuccess: () => {
      message.success('等级已删除');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '删除失败'),
  });

  const openCreate = () => {
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ sortOrder: levels.length, isDefault: false });
  };

  const openEdit = (level: LevelRow) => {
    setEditing(level);
    form.setFieldsValue({
      code: level.code,
      name: level.name,
      description: level.description ?? '',
      isDefault: level.isDefault,
      sortOrder: level.sortOrder,
    });
  };

  const submit = async () => {
    try {
      const v = await form.validateFields();
      save.mutate({
        code: editing?.code,
        values: {
          // code 仅创建时可设定；更新走 :code 路径参数，body 不带 code
          ...(editing ? {} : { code: v.code }),
          name: v.name,
          description: v.description || undefined,
          isDefault: v.isDefault ?? false,
          sortOrder: v.sortOrder ?? 0,
        },
      });
    } catch {
      /* 校验失败 */
    }
  };

  const askDelete = (level: LevelRow) => {
    modal.confirm({
      title: '删除等级',
      content: level.isDefault
        ? '默认等级不可删除，请先将其他等级设为默认。'
        : `确认删除等级「${level.name}」？该操作不可撤销。`,
      okButtonProps: { danger: true, disabled: level.isDefault },
      okText: '删除',
      cancelText: '取消',
      onOk: () => remove.mutateAsync(level.code),
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Text type="secondary">
          等级由数据库驱动，可自由增删。默认等级为新用户注册时的初始等级。
        </Typography.Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          新建等级
        </Button>
      </div>

      {isLoading ? (
        <Card loading />
      ) : levels.length === 0 ? (
        <Card>
          <Empty description="暂无会员等级" />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {levels.map((level, idx) => {
            const color = PALETTE[idx % PALETTE.length];
            return (
              <Col key={level.id} xs={24} sm={12} lg={8} xxl={6}>
                <Card
                  style={{ borderTop: `3px solid ${color}`, height: '100%' }}
                  styles={{ body: { padding: 20 } }}
                  actions={[
                    <Tooltip title="编辑" key="edit">
                      <EditOutlined onClick={() => openEdit(level)} />
                    </Tooltip>,
                    <Tooltip title={level.isDefault ? '默认等级不可删除' : '删除'} key="del">
                      <DeleteOutlined
                        style={{ color: level.isDefault ? '#c9ced3' : '#ff4d4f' }}
                        onClick={() => askDelete(level)}
                      />
                    </Tooltip>,
                  ]}
                >
                  <Space align="start" size={12}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 12,
                        background: `${color}1a`,
                        color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                      }}
                    >
                      <CrownOutlined />
                    </div>
                    <div>
                      <Space size={6}>
                        <Typography.Text strong style={{ fontSize: 16 }}>
                          {level.name}
                        </Typography.Text>
                        {level.isDefault && <Tag color="teal">默认</Tag>}
                      </Space>
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
                          {level.code}
                        </Typography.Text>
                      </div>
                    </div>
                  </Space>
                  <Typography.Paragraph
                    type="secondary"
                    ellipsis={{ rows: 2 }}
                    style={{ marginTop: 12, marginBottom: 0, minHeight: 40, fontSize: 13 }}
                  >
                    {level.description || '暂无描述'}
                  </Typography.Paragraph>
                  <div style={{ marginTop: 8 }}>
                    <Tag style={{ fontSize: 11 }}>排序 {level.sortOrder}</Tag>
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}

      <Modal
        title={editing ? '编辑等级' : '新建等级'}
        open={creating || editing !== null}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={submit}
        okText="保存"
        cancelText="取消"
        confirmLoading={save.isPending}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="code"
            label="等级标识（code）"
            tooltip="唯一英文标识，创建后不可修改，如 free / premium"
            rules={[
              { required: true, message: '请输入等级标识' },
              { pattern: /^[a-z0-9_]+$/, message: '仅允许小写字母、数字、下划线' },
            ]}
          >
            <Input placeholder="如 premium" disabled={!!editing} />
          </Form.Item>
          <Form.Item name="name" label="等级名称" rules={[{ required: true, message: '请输入等级名称' }]}>
            <Input placeholder="如 高级会员" />
          </Form.Item>
          <Form.Item name="description" label="等级描述">
            <Input.TextArea rows={2} placeholder="简要描述该等级的定位" />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="sortOrder" label="排序" tooltip="数值越小越靠前">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isDefault" label="设为默认等级" valuePropName="checked" tooltip="新用户注册时的初始等级，全局仅一个">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Typography.Text type="secondary" style={{ fontSize: 12, color: TEAL }}>
            提示：设为默认后，原默认等级将自动取消默认状态。
          </Typography.Text>
        </Form>
      </Modal>
    </div>
  );
}
